/**
 * IntegrationForm Component
 *
 * Dynamic form renderer for integration setup based on IntegrationMeta field definitions.
 * Supports all field types: text, password, textarea, checkbox, select, hidden.
 *
 * Features:
 * - Dynamic field rendering based on field.type
 * - Form validation with inline error display
 * - Conditional fields based on dependsOn
 * - Loading states for async operations
 * - Support for create and edit modes
 */

import React, { useCallback, useEffect, useState } from 'react';

import { Type as InputType } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import type { IntegrationField } from '@/features/integrations/types/field.types';
import type { SimpleIntegration } from '@/features/integrations/types/integration.types';
import { cn } from '@/utils/classname';

export interface IntegrationFormProps {
  /** Integration metadata with field definitions */
  integration: SimpleIntegration;
  /** Pre-filled form values for edit mode */
  initialValues?: Record<string, string>;
  /** Form submission handler */
  onSubmit: (values: Record<string, string>) => Promise<void>;
  /** Cancel button handler */
  onCancel?: () => void;
  /** Loading state for submit button */
  isLoading?: boolean;
  /** Form mode (affects button labels) */
  mode?: 'create' | 'edit';
  /** Test connection button handler */
  onTest?: (values: Record<string, string>) => Promise<void>;
  /** Test connection loading state */
  isTesting?: boolean;
}

interface FormState {
  values: Record<string, string>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
}

/**
 * IntegrationForm - Dynamic form component for integration setup
 */
export function IntegrationForm({
  integration,
  initialValues = {},
  onSubmit,
  onCancel,
  isLoading = false,
  mode = 'create',
  onTest,
  isTesting = false,
}: IntegrationFormProps) {
  // Initialize form state with default values from fields
  const [formState, setFormState] = useState<FormState>(() => {
    const values: Record<string, string> = {};

    integration.inputs.forEach(field => {
      values[field.name] = initialValues[field.name] ?? field.value ?? '';
    });

    return {
      values,
      errors: {},
      touched: {},
    };
  });

  // Update form values when initialValues change (for edit mode)
  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormState(prev => ({
        ...prev,
        values: { ...prev.values, ...initialValues },
      }));
    }
  }, [initialValues]);

  /**
   * Validate a single field based on its configuration
   */
  const validateField = useCallback(
    (field: IntegrationField, value: string): string | null => {
      // Required field validation
      if (field.required && !value) {
        return `${field.label || field.name} is required`;
      }

      // Pattern validation for text fields
      if ('pattern' in field && field.pattern && value) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(value)) {
          return `Invalid format for ${field.label || field.name}`;
        }
      }

      return null;
    },
    []
  );

  /**
   * Validate all visible fields in the form
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    integration.inputs.forEach(field => {
      // Skip hidden fields and fields with dependencies not met
      if (field.hidden) return;

      if ('dependsOn' in field && field.dependsOn) {
        const dependsOnValue = formState.values[field.dependsOn];
        if (!dependsOnValue || dependsOnValue === 'false') return;
      }

      const error = validateField(field, formState.values[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    setFormState(prev => ({ ...prev, errors: newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [integration.inputs, formState.values, validateField]);

  /**
   * Handle field value change
   */
  const handleChange = useCallback(
    (name: string, value: string) => {
      setFormState(prev => {
        const newValues = { ...prev.values, [name]: value };
        const newErrors = { ...prev.errors };

        // Clear error for this field if it now has a value
        if (value && newErrors[name]) {
          delete newErrors[name];
        }

        // If this field is a dependency for other fields, clear dependent field values
        integration.inputs.forEach(field => {
          if ('dependsOn' in field && field.dependsOn === name) {
            // If dependency is cleared or set to false, clear the dependent field
            if (!value || value === 'false') {
              newValues[field.name] = '';
            }
          }
        });

        return {
          ...prev,
          values: newValues,
          errors: newErrors,
        };
      });
    },
    [integration.inputs]
  );

  /**
   * Handle field blur - mark as touched and validate
   */
  const handleBlur = useCallback(
    (name: string) => {
      setFormState(prev => {
        const field = integration.inputs.find(f => f.name === name);
        if (!field) return prev;

        const error = validateField(field, prev.values[name]);
        const newErrors = { ...prev.errors };

        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }

        return {
          ...prev,
          touched: { ...prev.touched, [name]: true },
          errors: newErrors,
        };
      });
    },
    [integration.inputs, validateField]
  );

  const transformFormValues = useCallback(
    (values: Record<string, string>): Record<string, string> => {
      const transformed = { ...values };

      integration.inputs.forEach(field => {
        if ('multilineList' in field && field.multilineList) {
          const rawValue = transformed[field.name] || '';
          transformed[field.name] = rawValue
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .join(',');
        }
      });

      return transformed;
    },
    [integration.inputs]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        const allTouched: Record<string, boolean> = {};
        integration.inputs.forEach(field => {
          allTouched[field.name] = true;
        });
        setFormState(prev => ({ ...prev, touched: allTouched }));
        return;
      }

      await onSubmit(transformFormValues(formState.values));
    },
    [
      validateForm,
      formState.values,
      onSubmit,
      integration.inputs,
      transformFormValues,
    ]
  );

  const handleTest = useCallback(async () => {
    if (!validateForm()) {
      const allTouched: Record<string, boolean> = {};
      integration.inputs.forEach(field => {
        allTouched[field.name] = true;
      });
      setFormState(prev => ({ ...prev, touched: allTouched }));
      return;
    }

    if (onTest) {
      await onTest(transformFormValues(formState.values));
    }
  }, [
    validateForm,
    formState.values,
    onTest,
    integration.inputs,
    transformFormValues,
  ]);

  /**
   * Check if a field should be visible based on dependencies
   */
  const isFieldVisible = useCallback(
    (field: IntegrationField): boolean => {
      if (field.hidden) return false;

      if ('dependsOn' in field && field.dependsOn) {
        const dependsOnValue = formState.values[field.dependsOn];

        // If hideTillDependsOn is true, hide until dependency has a value
        if ('hideTillDependsOn' in field && field.hideTillDependsOn) {
          return !!dependsOnValue && dependsOnValue !== 'false';
        }
      }

      return true;
    },
    [formState.values]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {integration.inputs.map(field => {
        if (!isFieldVisible(field)) return null;

        return (
          <FieldRenderer
            key={field.name}
            field={field}
            value={formState.values[field.name]}
            error={
              formState.touched[field.name]
                ? formState.errors[field.name]
                : undefined
            }
            onChange={handleChange}
            onBlur={handleBlur}
          />
        );
      })}

      <div className="flex justify-end gap-4">
        {onTest && (
          <Button
            type="button"
            onClick={handleTest}
            variant="outline"
            disabled={isTesting || isLoading}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
        )}
        {onCancel && (
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            disabled={isLoading}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isLoading || isTesting}
          onClick={() => {
            /* handled by form onSubmit */
          }}
        >
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
        </Button>
      </div>
    </form>
  );
}

/**
 * FieldRenderer - Renders individual form fields based on type
 */
interface FieldRendererProps {
  field: IntegrationField;
  value: string;
  error?: string;
  onChange: (name: string, value: string) => void;
  onBlur: (name: string) => void;
}

function FieldRenderer({
  field,
  value,
  error,
  onChange,
  onBlur,
}: FieldRendererProps) {
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(field.name, e.target.value);
    },
    [field.name, onChange]
  );

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field.name, e.target.checked ? 'true' : 'false');
    },
    [field.name, onChange]
  );

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(field.name, e.target.value);
    },
    [field.name, onChange]
  );

  const handleInputBlur = useCallback(() => {
    onBlur(field.name);
  }, [field.name, onBlur]);

  // Hidden field
  if (field.hidden) {
    return <input type="hidden" name={field.name} value={value} />;
  }

  // Checkbox field
  if ('type' in field && field.type === InputType.CHECKBOX) {
    return (
      <div className="flex items-start">
        <div className="flex h-5 items-center">
          <input
            id={field.name}
            name={field.name}
            type="checkbox"
            checked={value === 'true'}
            onChange={handleCheckboxChange}
            disabled={field.disabled}
            className="border-chariot-stroke text-chariot-brand-primary focus:ring-chariot-brand-primary h-4 w-4 rounded"
          />
        </div>
        <div className="ml-3 text-sm">
          <label
            htmlFor={field.name}
            className="text-chariot-text-primary font-medium"
          >
            {field.label}
          </label>
          {field.info?.text && (
            <p className="text-chariot-text-secondary">{field.info.text}</p>
          )}
        </div>
      </div>
    );
  }

  // Select field
  if ('type' in field && field.type === InputType.SELECT) {
    return (
      <div>
        <label
          htmlFor={field.name}
          className="text-chariot-text-primary mb-2 block text-sm font-medium"
        >
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>
        <select
          id={field.name}
          name={field.name}
          value={value}
          onChange={handleSelectChange}
          onBlur={handleInputBlur}
          disabled={field.disabled}
          className={cn(
            'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary block w-full rounded-md px-3 py-2 shadow-xs focus:ring-1 focus:outline-hidden sm:text-sm',
            error && 'border-red-500'
          )}
        >
          <option value="">{field.placeholder || 'Select an option'}</option>
          {field.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        {field.info?.text && !error && (
          <p className="text-chariot-text-secondary mt-1 text-sm">
            {field.info.text}
          </p>
        )}
      </div>
    );
  }

  // Text area field
  if ('type' in field && field.type === InputType.TEXT_AREA) {
    return (
      <div>
        <label
          htmlFor={field.name}
          className="text-chariot-text-primary mb-2 block text-sm font-medium"
        >
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>
        <textarea
          id={field.name}
          name={field.name}
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={field.placeholder}
          disabled={field.disabled}
          rows={6}
          className={cn(
            'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary block w-full rounded-md px-3 py-2 font-mono text-xs shadow-xs focus:ring-1 focus:outline-hidden sm:text-sm',
            error && 'border-red-500',
            'password' in field && field.password && 'password-mask'
          )}
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        {field.info?.text && !error && (
          <p className="text-chariot-text-secondary mt-1 text-sm">
            {field.info.text}
          </p>
        )}
        {field.info?.url && !error && (
          <a
            href={field.info.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-chariot-brand-primary mt-1 text-sm hover:underline"
          >
            Learn more →
          </a>
        )}
      </div>
    );
  }

  // Password field
  const isPassword = 'type' in field && field.type === InputType.PASSWORD;

  // Default: Text or Password input
  return (
    <div>
      <label
        htmlFor={field.name}
        className="text-chariot-text-primary mb-2 block text-sm font-medium"
      >
        {field.label}
        {field.required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <input
        id={field.name}
        name={field.name}
        type={isPassword ? 'password' : 'text'}
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={field.placeholder}
        disabled={field.disabled}
        className={cn(
          'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary focus:border-chariot-brand-primary focus:ring-chariot-brand-primary block w-full rounded-md px-3 py-2 shadow-xs focus:ring-1 focus:outline-hidden sm:text-sm',
          error && 'border-red-500'
        )}
      />
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      {field.info?.text && !error && (
        <p className="text-chariot-text-secondary mt-1 text-sm">
          {field.info.text}
        </p>
      )}
      {field.info?.url && !error && (
        <a
          href={field.info.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-chariot-brand-primary mt-1 inline-block text-sm hover:underline"
        >
          Learn more →
        </a>
      )}
    </div>
  );
}
